/**
 *
 * NewAccount
 *
 */
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNewAccountSlice } from './slice';
import { selectNewAccount } from './slice/selectors';
import { routes } from 'utils/routes';
import { Link } from 'react-router-dom';
import './styles.scss';
import { toast } from 'react-toast';
import { useQuery, validateEmail } from 'utils/commons';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useRecaptcha } from '../../hooks/useRecaptcha';

interface Props {}

export const NewAccount = memo((props: Props) => {
  const [emailId, setEmailId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validEmail, setValidEmail] = useState('');

  const [isAppsumo, setIsAppsumo] = useState(false);
  const [isAppsumoEmail, setIsAppsumoEmail] = useState(false);
  const [isTOSchecked, setTOSchecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { token: recaptchaToken, reset: resetRecaptcha } = useRecaptcha('register-recaptcha');

  const { getFieldProps, getFieldError } = useFormValidation();
  const { actions } = useNewAccountSlice();
  const registerAccountState = useSelector(selectNewAccount);
  const dispatch = useDispatch();
  const params = useQuery();

  const toggleTOSChecked = () => {
    setTOSchecked(value => !value);
  };

  const updateEmail = email => {
    const emailStateIs = validateEmail(email);
    if (emailStateIs) {
      setEmailId(email);
      setValidEmail('true');
    } else {
      setEmailId(email);
      setValidEmail('');
    }
  };

  useEffect(() => {
    const isAppSumo = params.get('appSumo');
    const queryEmail = params.get('email');
    const queryFirstName = params.get('firstName');
    const queryLastName = params.get('lastName');

    if (isAppSumo) {
      setIsAppsumoEmail(true);
      setIsAppsumo(true);
    } else {
      setIsAppsumoEmail(false);
    }

    if (queryEmail) {
      updateEmail(queryEmail || '');
    }

    if (queryFirstName) {
      setFirstName(queryFirstName);
    }

    if (queryLastName) {
      setLastName(queryLastName);
    }
  }, [params]);

  const onRegister = e => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!recaptchaToken) {
      toast.error('Please complete the reCAPTCHA verification.');
      return;
    }
    dispatch(
      actions.onRegister({
        req: {
          firstName: firstName,
          lastName: lastName,
          email: emailId,
          password: password,
          profileCompletion: false,
          recaptchaToken,
        },
        successCb: () => {
          if (isAppsumoEmail) {
            toast.success(
              'Account created successfully, thank you for purchasing ContractHub via AppSumo.',
            );
          }
        },
        errorCb: () => {
          resetRecaptcha();
        },
      }),
    );
  };

  return (
    <div className="login-wrapper">
      <div className="login-sales">
        <div className="sales-content">
          <h2>Why ContractHub?</h2>
          <div className="sales-item">
            <div className="sales-icon">🔗</div>
            <div>
              <h3>Powerful Integrations</h3>
              <p>Connect with webhooks and automate your entire contract workflow</p>
            </div>
          </div>
          <div className="sales-item">
            <div className="sales-icon">🎯</div>
            <div>
              <h3>Flexible Signing Experience</h3>
              <p>Choose between guided forms or traditional PDF signing for each recipient</p>
            </div>
          </div>
          <div className="sales-item">
            <div className="sales-icon">⚙️</div>
            <div>
              <h3>Custom Workflows</h3>
              <p>Build approval chains, internal signing, and multi-party workflows</p>
            </div>
          </div>
          <div className="sales-stats">
            <div className="sales-stat">
              <span className="sales-stat-number">2,940</span>
              <span className="sales-stat-label">Company Accounts</span>
            </div>
            <div className="sales-stat-divider"></div>
            <div className="sales-stat">
              <span className="sales-stat-number">29,644</span>
              <span className="sales-stat-label">Documents Signed</span>
            </div>
          </div>
        </div>
      </div>
      <div className="login-form-container">
        <div className="formHolder">
        <div className="loginBox p-lg">
          {registerAccountState.stepNo === 1 ? (
            <div className="reg-success-container">
              <div className="reg-success-icon">
                <div className="logoIMG">
                  <img
                    width="90"
                    src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/animated-logo-huge.gif"
                    alt=""
                  ></img>
                </div>
              </div>
              <h1 className="reg-success-title verify-title">
                🎉 Almost There!
              </h1>
              <div className="reg-success-text">
                <p className=" verify-subtitle">
                  We've sent a verification link to:
                </p>
                <code className="reg-email-code mb-20">{emailId}</code>

                <p>
                  Check your inbox for an activation email from us. Click the
                  link inside to activate your account and start using
                  ContractHub. If it’s not there in a few minutes, be sure to
                  check your spam or junk folder.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="logoIMG">
                <img
                  width={90}
                  src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/animated-logo-huge.gif"
                  alt=""
                />
              </div>

              <div className="title">
                {isAppsumo ? (
                  <>👋 Welcome Sumo-ling!</>
                ) : params.get('source') === 'guest-success' ? (
                  <>🎉 Secure Your Documents!</>
                ) : (
                  <>Create your account</>
                )}
              </div>

              <div className="subTitle">
                {isAppsumo
                  ? 'Complete your account setup to activate your AppSumo purchase and start using ContractHub.'
                  : params.get('source') === 'guest-success'
                  ? 'Create your free account to securely store and manage all your signed documents in one place.'
                  : 'Enter your information to get started with ContractHub'}
              </div>

              <form
                className="d-flex flex-column login-form"
                onSubmit={onRegister}
              >
                <div className="d-flex" style={{ gap: '12px' }}>
                  <div className="transitionedInputWrapper" style={{ flex: 1 }}>
                    <label className="inp">
                      <input
                        required
                        type="text"
                        placeholder="&nbsp;"
                        tabIndex={1}
                        {...getFieldProps('firstName', { required: true })}
                        value={firstName}
                        onChange={e => {
                          setFirstName(e.target.value);
                          getFieldProps('firstName', {
                            required: true,
                          }).onChange(e);
                        }}
                      />
                      <span className="label">First Name</span>
                      <span className="focus-bg"></span>
                    </label>
                    {getFieldError('firstName') && (
                      <div className="form-error-message">
                        {getFieldError('firstName')}
                      </div>
                    )}
                  </div>

                  <div className="transitionedInputWrapper" style={{ flex: 1 }}>
                    <label className="inp">
                      <input
                        required
                        type="text"
                        placeholder="&nbsp;"
                        tabIndex={2}
                        {...getFieldProps('lastName', { required: true })}
                        value={lastName}
                        onChange={e => {
                          setLastName(e.target.value);
                          getFieldProps('lastName', {
                            required: true,
                          }).onChange(e);
                        }}
                      />
                      <span className="label">Last Name</span>
                      <span className="focus-bg"></span>
                    </label>
                    {getFieldError('lastName') && (
                      <div className="form-error-message">
                        {getFieldError('lastName')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="transitionedInputWrapper">
                  <label className="inp">
                    <input
                      required
                      type="email"
                      placeholder="&nbsp;"
                      tabIndex={3}
                      disabled={isAppsumoEmail}
                      {...getFieldProps('email', {
                        required: true,
                        email: true,
                      })}
                      value={emailId}
                      onChange={e => {
                        updateEmail(e.target.value);
                        dispatch(actions.onClearEmailAlreadyExists());
                        getFieldProps('email', {
                          required: true,
                          email: true,
                        }).onChange(e);
                      }}
                    />
                    <span className="label">Email</span>
                    <span className="focus-bg"></span>
                  </label>
                  {(getFieldError('email') ||
                    registerAccountState.emailAlreadyRegistered) && (
                    <div className="form-error-message">
                      {getFieldError('email') || 'Email already registered'}
                    </div>
                  )}
                </div>

                <div className="transitionedInputWrapper">
                  <label className="inp password-field-container">
                    <input
                      required
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="&nbsp;"
                      tabIndex={4}
                      {...getFieldProps('password', {
                        required: true,
                        password: true,
                      })}
                      onChange={e => {
                        setPassword(e.target.value);
                        getFieldProps('password', {
                          required: true,
                          password: true,
                        }).onChange(e);
                      }}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setShowPassword(!showPassword);
                        }
                      }}
                    >
                      <i
                        className={
                          showPassword ? 'fi fi-rr-eye-crossed' : 'fi fi-rr-eye'
                        }
                      ></i>
                    </button>
                    <span className="label">Password</span>
                    <span className="focus-bg"></span>
                  </label>
                  <div className="reg-password-strength">
                    <span className="reg-password-hint">
                      <i className="fi fi-rr-shield-check"></i>
                      8-20 characters, 1 number, 1 letter, 1 symbol
                    </span>
                  </div>
                  {getFieldError('password') && (
                    <div className="form-error-message">
                      {getFieldError('password')}
                    </div>
                  )}
                </div>

                {password && (
                  <div className="transitionedInputWrapper">
                    <label className="inp password-field-container">
                      <input
                        required
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="&nbsp;"
                        tabIndex={5}
                        {...getFieldProps('confirmPassword', {
                          required: true,
                          confirmPassword: password,
                        })}
                        onChange={e => {
                          setConfirmPassword(e.target.value);
                          getFieldProps('confirmPassword', {
                            required: true,
                            confirmPassword: password,
                          }).onChange(e);
                        }}
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="password-toggle"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowConfirmPassword(!showConfirmPassword);
                          }
                        }}
                      >
                        <i
                          className={
                            showConfirmPassword
                              ? 'fi fi-rr-eye-crossed'
                              : 'fi fi-rr-eye'
                          }
                        ></i>
                      </button>
                      <span className="label">Confirm Password</span>
                      <span className="focus-bg"></span>
                    </label>
                    {getFieldError('confirmPassword') && (
                      <div className="form-error-message">
                        {getFieldError('confirmPassword')}
                      </div>
                    )}
                  </div>
                )}

                <div className="transitionedInputWrapper mb-17">
                  <label className="modern-checkbox-container">
                    <input
                      type="checkbox"
                      tabIndex={6}
                      checked={isTOSchecked}
                      onChange={toggleTOSChecked}
                      className="modern-checkbox-input"
                    />
                    <span className="modern-checkbox-checkmark"></span>
                    <span className="modern-checkbox-text">
                      I agree to the{' '}
                      <a
                        href="https://www.contracthub.com/terms-of-use/"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="link-text"
                      >
                        Terms
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://www.contracthub.com/privacy-policy/"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="link-text"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                </div>

                <div className="transitionedInputWrapper d-flex justify-content-center align-items-center" style={{ flexDirection: 'column', gap: '12px' }}>
                  <div id="register-recaptcha"></div>
                  <button
                    tabIndex={7}
                    className={`full-width mb-17 mt-20 primaryButton large-btn bold loginButton ${
                      registerAccountState.registering
                        ? 'roundSpinnerButton'
                        : ''
                    }`}
                    disabled={
                      registerAccountState.registering ||
                      !isTOSchecked ||
                      emailId === '' ||
                      firstName === '' ||
                      lastName === '' ||
                      password === '' ||
                      confirmPassword === '' ||
                      !validEmail ||
                      !recaptchaToken
                    }
                    type="submit"
                  >
                    {!registerAccountState.registering ? (
                      'Create Account'
                    ) : (
                      <i className="gg-spinner-two"></i>
                    )}
                  </button>
                </div>
              </form>

              {registerAccountState.error && (
                <div className="transitionedInputWrapper">
                  <div className="staticToast">
                    <img
                      width="50"
                      src="https://contracthub-static-assets.s3.ap-south-1.amazonaws.com/aniamted-error-icon-light.gif"
                      alt=""
                    />
                    <p>{registerAccountState.error}</p>
                  </div>
                </div>
              )}

              <div className="transitionedInputWrapper mb-0">
                <div className="text-info mt-20">
                  Already have an account?{' '}
                  <Link
                    to={routes.login}
                    className="link-text underline-type-1"
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            </>
          )}

          <div className="reg-footer mt-10">
            <div className="reg-copyright">
              ©2025{' '}
              <a
                href="https://www.contracthub.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                ContractHub, Inc.
              </a>{' '}
              All rights reserved.
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
});
